import React from "react";
import { motion } from "framer-motion";
import { Instagram, Mail } from "lucide-react";
import { IMAGES, CONTACT_EMAIL, INSTAGRAM_URL } from "@/lib/site-data";

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Image side */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] max-w-md mx-auto lg:mx-0">
            {/* Sage block behind */}
            <div className="absolute -top-6 -left-6 w-full h-full bg-brand-sage/25 rounded-[2rem]" aria-hidden="true" />
            {/* Periwinkle accent */}
            <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-brand-periwinkle rounded-full blur-2xl opacity-60" aria-hidden="true" />
            <img
              src={IMAGES.founderSade}
              alt="Sade, Founder and CEO of The Aligned SOULutions"
              className="relative w-full h-full object-cover rounded-[2rem] shadow-xl"
              data-testid="founder-image"
            />
          </div>
        </motion.div>

        {/* Text side */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <p className="font-sans text-xs md:text-sm tracking-[0.25em] uppercase text-brand-sage mb-5">
            Meet the founder
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-brand-ink mb-8">
            A decade of advocacy.{" "}
            <span className="italic text-brand-blue">A lifetime of purpose.</span>
          </h2>

          <div className="space-y-5 text-base md:text-lg text-brand-ink/80 leading-relaxed max-w-2xl">
            <p>
              I'm the Founder and CEO of <strong className="text-brand-ink">The Aligned SOULutions</strong>,
              a people-centered consulting and advisory firm supporting mission-driven
              organizations, small businesses, and community-rooted leaders who need
              hands-on, implementation-focused help to operate, grow, and create
              lasting impact.
            </p>
            <p>
              With over a decade of experience in advocacy, strategic communications,
              digital equity, and racial justice, I bring an{" "}
              <span className="text-brand-blue font-medium">empathy-centered, implementation-focused approach</span>{" "}
              to my work.
            </p>
            <p>
              I am deeply committed to helping justice system-impacted and underserved
              communities build sustainable, people-first solutions rooted in dignity,
              belonging, and impact.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-instagram-link"
              className="inline-flex items-center gap-2 text-sm text-brand-blue hover:text-brand-ink transition-colors group"
            >
              <Instagram className="w-4 h-4 group-hover:scale-110 transition-transform" />
              @thealignedsoulutions
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="about-email-link"
              className="inline-flex items-center gap-2 text-sm text-brand-blue hover:text-brand-ink transition-colors group"
            >
              <Mail className="w-4 h-4 group-hover:scale-110 transition-transform" />
              {CONTACT_EMAIL}
            </a>
          </div>

          {/* Signature-style line */}
          <div className="mt-10 pt-8 border-t border-brand-blue/15 max-w-md">
            <p className="font-serif italic text-2xl text-brand-blue">— Sade</p>
            <p className="text-xs tracking-[0.2em] uppercase text-brand-sage mt-1">
              Founder & CEO
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
